import {track} from './journeyEvents';
import { API_BASE_URL } from './api';
import { authService } from './auth';
import { captureHeaders } from './captureSession';

export interface Features { wallet: boolean; face_video: boolean; credit_checkout: boolean; usage_charging: boolean; ai_video?: boolean; animation_studio?: boolean; text_animation?: boolean }
export const disabledFeatures: Features = {wallet: false, face_video: false, credit_checkout: false, usage_charging: false, ai_video: false, animation_studio: false, text_animation: false};
export async function features(signal?: AbortSignal): Promise<Features> {
  const response = await fetch(`${API_BASE_URL}/api/features`, {cache: 'no-store', signal});
  if (response.status === 404) return disabledFeatures;
  if (!response.ok) throw new Error('Could not load service availability.');
  return response.json();
}
export class CaptureAccountError extends Error {
  constructor(message: string, public readonly status: number) { super(message); this.name = 'CaptureAccountError'; }
}
export async function accountRequest(path: string, init: RequestInit = {}): Promise<Response> {
  const token = authService.getToken();
  const capture=path.startsWith('face-video-mocap/');
  const download=path.endsWith('/result');
  if(!token)throw new CaptureAccountError('Create an account or sign in before using capture.',401);
  const headers=capture ? await captureHeaders(init.headers,download) : new Headers(init.headers);
  if(token)headers.set('Authorization',`Bearer ${token}`);
  let response: Response;
  try { response = await fetch(`${API_BASE_URL}/api/${path}`, {...init, headers, cache: 'no-store'}); }
  catch (error) {
    if ((error as Error).name === 'AbortError') throw error;
    throw new Error('Connection interrupted. Check the history before submitting again.');
  }
  if (!response.ok) {
    const data = await response.json().catch(() => null);
    const detail = data?.detail;
    throw new CaptureAccountError(response.status === 401 ? 'Your session expired. Sign in again.' :
      response.status === 413 ? (path.startsWith('animation-studio/') ? (typeof detail === 'string' ? detail : 'An animation asset exceeds the upload limit.') : path.startsWith('ai-video/') ? 'The image exceeds the 10 MiB upload limit.' : 'The video exceeds the upload limit. Face Mocap accepts files up to 100 MiB.') :
      response.status === 504 ? 'The upload timed out. Check your capture history before trying again.' :
      typeof detail === 'string' ? detail : detail?.message || 'Service temporarily unavailable.', response.status);
  }
  return response;
}
export interface FaceJob {
  job_id: string; filename: string; state: string; stage: string | null;
  created_at: string; result_available: boolean; retryable: boolean;
  error: {code: string; message: string} | null;
  metadata: {frames?: number; fps?: number; frames_with_face?: number} | null;
}
export const faceVideoService = {
  async list(signal?: AbortSignal): Promise<FaceJob[]> {
    return (await (await accountRequest('face-video-mocap/jobs?limit=50', {signal})).json()).jobs;
  },
  async get(id: string, signal?: AbortSignal): Promise<FaceJob> {
    return (await accountRequest(`face-video-mocap/jobs/${encodeURIComponent(id)}`, {signal})).json();
  },
  async estimate(video:File,signal?:AbortSignal):Promise<{estimated_credits:number;input_nanoseconds:string}>{const body=new FormData();body.append('video',video);return (await accountRequest('face-video-mocap/estimate',{method:'POST',body,signal})).json();},
  async cancel(id:string):Promise<FaceJob>{return (await accountRequest(`face-video-mocap/jobs/${encodeURIComponent(id)}/cancel`,{method:'POST'})).json();},
  async submit(video: File, key: string, expectedCredits?:number): Promise<FaceJob> {
    track('upload_started',{surface:'face',modality:'FACE_MOCAP'});
    const body = new FormData(); body.append('video', video);
    try{return (await accountRequest('face-video-mocap/jobs', {method: 'POST', body, headers: {'Idempotency-Key': key,...(expectedCredits!==undefined ? {'X-Expected-Credits':String(expectedCredits)} : {})}})).json();}catch(error){track('upload_failed',{surface:'face',error_code:error instanceof CaptureAccountError && error.status===422?'video_invalid':'network'});throw error;}
  },
  async download(id: string): Promise<Blob> {
    track('download_attempted',{surface:'face',job_id:id});
    const blob=await (await accountRequest(`face-video-mocap/jobs/${encodeURIComponent(id)}/result`)).blob();track('file_received',{surface:'face',job_id:id});return blob;
  },
};
